import { DefaultError, InfiniteData, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';

import { useProfile } from 'context/profile';
import { Analytics, retrieveAnalytics } from 'services/api/insights/analytics';
import { getAnalyticsFilters, useInsightsStore } from 'stores/insightsStore';

import { Dimensions, Metrics, Sources } from '../constants';

type QueryResult = Awaited<ReturnType<typeof retrieveAnalytics>>;

export type AnalyticsQueryParams = {
  source: Sources;
  dimensions?: (Dimensions | string)[];
  metrics: Metrics[];
  sortBy?: string;
  page?: number;
  pageSize?: number;
};

const defaultOptions = {
  getNextPageParam: () => null,
  initialPageParam: null,
};

export default function useAnalyticsInfiniteQuery(
  queryParams: AnalyticsQueryParams,
  options: Omit<
    UseInfiniteQueryOptions<QueryResult, DefaultError, InfiniteData<Analytics, Analytics>>,
    'queryKey' | 'queryFn'
  > = defaultOptions,
  includePriorPeriod = true
) {
  const { profile } = useProfile();
  const analyticsFilters = useInsightsStore((state) => getAnalyticsFilters(state, profile, includePriorPeriod));

  return useInfiniteQuery({
    queryKey: ['analytics', queryParams, analyticsFilters],
    queryFn: async ({ pageParam }) =>
      retrieveAnalytics({ ...queryParams, ...(pageParam as AnalyticsQueryParams) }, analyticsFilters),
    ...options,
  });
}
